<template>
    <Card :title="$t('labels.product_groups')">
        <NavTab class="nav-tabs-bottom mb-0" has-wrapper>
            <NavTabItem :href="`#${tab.id}`" :ref="setTabRef" v-for="tab in tabs" :key="tab.id">{{ tab.name }}</NavTabItem>
            <template #content>
                <NavTabPane id="products">
                    <div class="card-body d-flex flex-wrap" :style="{gap: '.5rem'}">
                        <AppButton color="teal" @click="$refs.UpdateGroupProductDataModal.show(selectedProducts)" :disabled="selectedProductUuids.length === 0">
                            {{ $t('buttons.update_group_product_data') }}
                        </AppButton>
                        <AppButton color="teal" @click="$refs.AssignValuesBasedOnProductInformation.show(selectedProductUuids)" :disabled="selectedProductUuids.length === 0">
                            <i class="icon-database2 mr-2"></i>
                            {{ $t("buttons.assign_values_based_on_product_information") }}
                        </AppButton>
                        <router-link class="btn btn-link" :to="{ name: 'CopyGroupProductsPage', params: { groupId: groupKey } }">
                            <i class="icon-copy3 mr-2"></i>
                            {{ $t("buttons.copy") }}
                        </router-link>
                    </div>
                    <div class="table-responsive shadow-0 mb-0">
                        <table class="table table-bordered">
                            <thead>
                                <tr>
                                    <th width="60px">
                                        <AppCheckbox :model-value="allProductUuidsIsSelected" @update:model-value="selectAllProductUuids"/>
                                    </th>
                                    <th width="78px">{{ $t('table.titles.action') }}</th>
                                    <th width="250px">{{ $t('table.titles.product') }}</th>
                                    <th v-for="filter in variableFilters" :style="{ minWidth: '200px' }">
                                        <div class="text-nowrap">
                                            {{ filter.filterName + (filter.measurementUnit ? ` (${filter.measurementUnit})` : '') }}
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="product in products">
                                    <td>
                                        <AppCheckbox :model-value="Object.keys(selectedProducts).includes(product.uuid)" @update:model-value="selectProductUuid(product)"/>
                                    </td>
                                    <td>
                                        <AppButton
                                            color="teal"
                                            :icon="true"
                                            @click="$refs.EditProductFiltersModal.show(product)">
                                            <i class="icon-pencil"></i>
                                        </AppButton>
                                    </td>
                                    <td>
                                        <div class="d-flex product-cell">
                                            <img 
                                                class="table-image mr-2 " 
                                                :src="product.picture_url ?? PLACEHOLDER_IMAGE_URL" 
                                                :alt="product.name"
                                            />
                                            <div class="text-container">
                                                <router-link :to="{ name: 'Product', params: { integrationProductUuid: product.uuid } }" class="d-block text-nowrap">
                                                    {{ product.name }}
                                                </router-link>
                                                <span class="text-muted text-smaller d-block">
                                                    {{ product.sku }}
                                                </span>
                                            </div>
                                        </div>
                                    </td>
                                    <td v-for="filter in variableFilters">
                                        <AppNullableCell>
                                            <template v-if="product.filters[filter.filterUuid]" #default>
                                                <div class="text-nowrap">
                                                    {{ getFilterItemsName(product.filters[filter.filterUuid]) }}
                                                </div>
                                            </template>
                                        </AppNullableCell>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </NavTabPane>
                <NavTabPane id="filters">
                    <div class="card-body">
                        <AppButton color="teal" @click="$refs.AddIntegrationGroupFilterModal.show(products)">
                            <i class="icon-plus2 mr-2"></i>
                            {{ $t('buttons.add') }}
                        </AppButton>
                    </div>
                    <div class="table-responsive shadow-0 mb-0">
                        <table class="table table-fixed table-bordered">
                            <thead>
                                <tr>
                                    <th width="127px">{{ $t('table.titles.action') }}</th>
                                    <th>{{ $t('table.titles.filter') }}</th>
                                    <th>{{ $t('table.titles.default_filter_item') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="filter in filters">
                                    <td>
                                        <AppButton
                                            color="danger"
                                            :icon="true"
                                            @click="deleteFilter(filter)"
                                            class="mr-2">
                                            <i class="icon-trash"></i>
                                        </AppButton>
                                        <AppButton
                                            color="teal"
                                            :icon="true"
                                            @click="$refs.EditIntegrationGroupFilterModal.show(products, filter)">
                                            <i class="icon-pencil"></i>
                                        </AppButton>
                                    </td>
                                    <td>{{ filter.filterName + (filter.measurementUnit ? ` (${filter.measurementUnit})` : '') }}</td>
                                    <td>
                                        <AppNullableCell>
                                            <template v-if="checkFilterValuesAreSame(filter.filterUuid)" #default>
                                                <div class="text-nowrap">
                                                    {{ getFilterItemsName(products[0].filters[filter.filterUuid]) }}
                                                </div>
                                            </template>
                                        </AppNullableCell>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </NavTabPane>
            </template>
        </NavTab>
    </Card>
    <AddIntegrationGroupFilterModal ref="AddIntegrationGroupFilterModal" :category="category" :integration="integration" @filterAdded="fetchProducts" :available-filter-uuids="Object.values(filters).map(filter => filter.filterUuid)" :getFilterName="getFilterName" />
    <EditIntegrationGroupFilterModal ref="EditIntegrationGroupFilterModal" :category="category" :integration="integration" @filterUpdated="fetchProducts" :getFilterName="getFilterName" />
    <EditProductFiltersModal ref="EditProductFiltersModal" @product-filters-updated="fetchProducts" />
    <UpdateGroupProductDataModal ref="UpdateGroupProductDataModal" @products-updated="fetchProducts" :integration="integration" />
    <AssignValuesBasedOnProductInformation
        ref="AssignValuesBasedOnProductInformation"
        :product-filters="{
            group_keys: [this.groupKey]
        }"
        :main-integration-language="integration.language_aliases[0]"
        :integration-category="category"
        :getFilterName="getFilterName"
        @updated:filter-items="() => {fetchProducts(), selectedProducts = {}}"
        :integration-service="integration.service"
    />
</template>
<script>
import Card from "../../../../components/new/CardComponent.vue";
import AppNullableCell from "../../../components/tables/AppNullableCell.vue";
import { PLACEHOLDER_IMAGE_URL } from "../../../../constants";
import FormGroup from "../../../../components/new/form/group/FormGroup.vue";
import Input from "../../../../components/new/form/Input.vue";
import AddIntegrationGroupFilterModal from "../modals/AddIntegrationGroupFilterModal.vue";
import AppButton from "../../../../components/new/button/AppButton.vue";
import EditIntegrationGroupFilterModal from "../modals/EditIntegrationGroupFilterModal.vue";
import EditProductFiltersModal from "../modals/EditProductFiltersModal.vue";
import AppCheckbox from "../../../../components/new/form/checkbox/AppCheckbox.vue";
import UpdateGroupProductDataModal from "../modals/UpdateGroupProductDataModal.vue";
import NavTab from "../../../components/tabs/NavTab.vue";
import NavTabItem from "../../../components/tabs/NavTabItem.vue";
import NavTabPane from "../../../components/tabs/NavTabPane.vue";
import AssignValuesBasedOnProductInformation from "../modals/AssignValuesBasedOnProductInformation.vue";

import { httpClient } from '../../../../services/HttpClient';
import { chunk } from "../../../../utils";
import MessageNotificator from "../../../../services/MessageNotificator";

export default {
    name: "ProductGroupTable",
    components: {
        Card, AppNullableCell, FormGroup, Input, AddIntegrationGroupFilterModal, AppButton, EditIntegrationGroupFilterModal,
        EditProductFiltersModal, AppCheckbox, UpdateGroupProductDataModal, NavTab, NavTabItem, NavTabPane,
        AssignValuesBasedOnProductInformation
    },
    props: {
        integration: {
            type: Object,
            required: true
        },
        groupKey: {
            type: String,
            required: true
        },
        category: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            products: [],
            filters: {},
            selectedProducts: {},
            tabs: [
                { id: 'products', name: this.$t('labels.products') },
                { id: 'filters', name: this.$t('labels.filters') }
            ],
            tabRefs: []
        }
    },
    methods: {
        getFilterName(filter) {
            if (!filter) {
                return '';
            }
            
            let name = filter.name;

            if (filter.measurement_unit) {
                name += ` (${filter.measurement_unit})`;
            }

            return name;
        },
        setTabRef(el) {
            if (el) {
                this.tabRefs.push(el);
            }
        },
        async fetchProducts() {
            let products = [];
            let filters = {};

            let totalProductsCount = 0;

            await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                group_keys: [this.groupKey],
                limit: 1
            }).then(response => {
                totalProductsCount = response.data.meta.total;
            });

            for (let i = 0; i < totalProductsCount; i += 10) {
                await httpClient.get(`/integrations/${this.$route.params.integrationUuid}/products`, {
                    group_keys: [this.groupKey],
                    limit: 10,
                    page: Math.floor(i / 10) + 1,
                    extra: 1,
                    extra_fields: ['integrationFilterItems']
                }).then(response => {
                    products.push(...response.data.data);
                });
            }

            for (let product of products) {
                product.filters = this.groupFilterItemsByFilterUuid(product);

                product.filterIdsByFilterUuids = Object.entries(product.filters).reduce((acc, [filterUuid, filterItems]) => {
                    acc[filterUuid] = filterItems.map(item => item.id).join('-');
                    return acc;
                }, {});

                Object.entries(product.filters).forEach(([filterUuid, filterItems]) => {
                    if (!filters[filterUuid]) {
                        filters[filterUuid] = {
                            filterUuid: filterUuid,
                            filterName: filterItems[0].integration_filter.name,
                            measurementUnit: filterItems[0].integration_filter.measurement_unit
                        }
                    }
                });
            }

            this.products = products;

            filters = Object.values(filters).sort((a, b) => a.filterName.localeCompare(b.filterName)).reduce((acc, filter) => {
                acc[filter.filterUuid] = filter;
                return acc;
            }, {});

            this.filters = filters;
        },
        groupFilterItemsByFilterUuid(product) {
            let filterItems = {};

            product.integration_filter_items.reduce((acc, filterItem) => {
                if (acc[filterItem.integration_filter_uuid]) {
                    acc[filterItem.integration_filter_uuid].push(filterItem);
                } else {
                    acc[filterItem.integration_filter_uuid] = [filterItem];
                }
                return acc;
            }, filterItems);

            return filterItems;
        },
        getFilterItemsName(filterItems) {
            return filterItems.map(item => item.name).join(', ');
        },
        checkFilterValuesAreSame(filterUuid) {
            if (this.products.length === 0) return true;

            const firstValue = this.products[0].filterIdsByFilterUuids[filterUuid];

            return this.products.every(product => 
                product.filterIdsByFilterUuids[filterUuid] === firstValue
            );
        },
        async deleteFilter(deleteFilter) {
            new MessageNotificator().confirm(this.$t('alerts.delete_filter_from_group'), async () => {
                let requestData = this.products.map(product => ({
                    method: 'PATCH',
                    path: `/integrations/products/${product.uuid}`,
                    body: {
                        filter_item_uuids: Object.values(product.filters)
                            .flat()
                            .filter(item => item.integration_filter_uuid !== deleteFilter.filterUuid)
                            .map(item => item.uuid)
                    }
                }))

                await Promise.all(chunk(requestData, 10).map(async (chunk) => {
                    await httpClient.post('/batch', {
                        data: chunk
                    });
                }));

                this.fetchProducts();
            });
        },
        selectProductUuid(product) {
            if (Object.keys(this.selectedProducts).includes(product.uuid)) {
                delete this.selectedProducts[product.uuid];
            } else {
                this.selectedProducts[product.uuid] = product;
            }
        },
        selectAllProductUuids() {
            if (this.allProductUuidsIsSelected) {
                this.selectedProducts = {};
            } else {
                this.selectedProducts = this.products.reduce((acc, product) => {
                    acc[product.uuid] = product;
                    return acc;
                }, {});
            }
        }
    },
    computed: {
        allProductUuidsIsSelected() {
            return Object.keys(this.selectedProducts).length === this.products.length;
        },
        variableFilters() {
            return Object.values(this.filters)
                .filter(filter => this.checkFilterValuesAreSame(filter.filterUuid) === false)
                .sort((a, b) => a.filterName.localeCompare(b.filterName));
        },
        selectedProductUuids() {
            return Object.keys(this.selectedProducts);
        }
    },
    watch: {
        groupKey: {
            immediate: true,
            handler() {
                this.products = []
                this.filters = {}
                this.fetchProducts()
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.tabRefs[0].click()
        });
    },
    beforeUpdate() {
        this.tabRefs = []
    },
}
</script>
<style scoped>
.table-image {
    width: 38px;
    height: 38px;
}
.product-cell {
    max-width: 100%;
    width: 100%;
}
.text-container {
    min-width: 0;
    overflow: hidden;
    flex: 1;
}
.text-nowrap {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.text-smaller {
    font-size: 12px;
    line-height: 18px;
}
</style>